import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

async function publicRequest(path, options = {}) {
  const res = await fetch(`/api${path}`, {
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

const PRIORITY_COLORS = { urgent: '#dc2626', high: '#ea580c', normal: '#64748b', low: '#94a3b8' };

export default function OutsourcedTaskPage() {
  const { token } = useParams();
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicRequest(`/public/outsourced/${token}`)
      .then(data => { setTask(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [token]);

  async function handleComplete() {
    setError('');
    setSubmitting(true);
    try {
      await publicRequest(`/public/outsourced/${token}/complete`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      });
      setDone(true);
    } catch (err) {
      setError(err.message || 'Failed to complete task');
    }
    setSubmitting(false);
  }

  const shell = (children) => (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16 }}>
      <div style={{ width: '100%', maxWidth: 480 }}>{children}</div>
    </div>
  );

  if (loading) {
    return shell(<div style={{ textAlign: 'center', fontSize: 18, fontWeight: 600, color: '#0f172a' }}>Loading task…</div>);
  }

  if (error && !task) {
    return shell(
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>&#128683;</div>
        <div style={{ fontSize: 18, fontWeight: 600, color: '#0f172a', marginBottom: 8 }}>Task Not Found</div>
        <div style={{ fontSize: 14, color: '#64748b' }}>{error}</div>
      </div>
    );
  }

  if (done || task.already_completed) {
    return shell(
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>&#9989;</div>
        <div style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>
          {done ? 'Task Complete!' : 'Already Completed'}
        </div>
        <div style={{ fontSize: 14, color: '#64748b', lineHeight: 1.6 }}>
          {done
            ? `Thanks, ${task.recipient_name || 'partner'}! ${task.assigned_by || 'The team'} has been notified.`
            : 'This task has already been marked complete. Nothing else to do here.'}
        </div>
        <div style={{ marginTop: 24, fontSize: 12, color: '#94a3b8' }}>
          Apollo Towing LLC &bull; Changing the Towing Industry
        </div>
      </div>
    );
  }

  const due = task.due_date
    ? new Date(task.due_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
    : null;

  return shell(
    <div>
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <img src="/logo-agreement.png" alt="Apollo Towing" style={{ width: 260, maxWidth: '80%' }} />
      </div>

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '24px 24px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>
          Task assigned to {task.recipient_name}{task.assigned_by ? ` by ${task.assigned_by}` : ''}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <div style={{ fontSize: 19, fontWeight: 700, color: '#0f172a' }}>{task.title}</div>
          {task.priority && task.priority !== 'normal' && (
            <span style={{
              padding: '2px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700,
              textTransform: 'uppercase', color: '#fff',
              background: PRIORITY_COLORS[task.priority] || '#64748b',
            }}>{task.priority}</span>
          )}
        </div>
        {task.fields?.length > 0 ? (
          <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12, marginBottom: 12 }}>
            {task.fields.map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '5px 0', alignItems: 'baseline' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, minWidth: 110, flexShrink: 0 }}>
                  {f.label}
                </div>
                <div style={{ fontSize: 14, color: '#0f172a', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{f.value}</div>
              </div>
            ))}
          </div>
        ) : task.description && (
          <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.65, whiteSpace: 'pre-wrap', marginBottom: 12 }}>
            {task.description}
          </div>
        )}
        {due && (
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>
            <strong style={{ color: '#0f172a' }}>Due:</strong> {due}
          </div>
        )}
        {task.files?.some(f => f.is_image) && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Photos</div>
            {task.files.filter(f => f.is_image).map((f, i) => (
              <a key={i} href={f.url} target="_blank" rel="noreferrer">
                <img src={f.url} alt={f.name} style={{ maxWidth: '100%', borderRadius: 8, border: '1px solid #e2e8f0', marginBottom: 8, display: 'block' }} />
              </a>
            ))}
          </div>
        )}
        {task.files?.some(f => !f.is_image) && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>Documents</div>
            {task.files.filter(f => !f.is_image).map((f, i) => (
              <a key={i} href={f.url} target="_blank" rel="noreferrer" style={{
                display: 'block', fontSize: 14, color: '#1d4ed8', textDecoration: 'none',
                padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 6,
              }}>
                📎 {f.name}
              </a>
            ))}
          </div>
        )}

        <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 18, paddingTop: 18 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', display: 'block', marginBottom: 6 }}>
            Notes (optional)
          </label>
          <textarea
            value={note}
            onChange={e => setNote(e.target.value)}
            placeholder="Anything the team should know about the completed work…"
            rows={3}
            style={{
              width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 14,
              border: '1px solid #d1d5db', borderRadius: 8, resize: 'vertical', fontFamily: 'inherit',
            }}
          />
          {error && <div style={{ fontSize: 13, color: '#dc2626', marginTop: 8 }}>{error}</div>}
          <button
            onClick={handleComplete}
            disabled={submitting}
            style={{
              width: '100%', marginTop: 14, padding: '14px 0', fontSize: 16, fontWeight: 700,
              borderRadius: 10, background: '#16a34a', color: '#fff', border: 'none',
              cursor: 'pointer', opacity: submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'Completing…' : '✓ Mark Task Complete'}
          </button>
        </div>
      </div>

      <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: '#94a3b8' }}>
        Apollo Towing LLC &bull; Changing the Towing Industry
      </div>
    </div>
  );
}
